import React, { useContext } from 'react';

import cx from 'classnames';
import { differenceInCalendarDays } from 'date-fns';

import itemStyles from 'components/Pages/Admin/GuestPortal/Flights/FlightsTable/index.module.css';
import { getMinutesAndHoursFormat } from 'components/Pages/Admin/GuestPortal/Flights/utils';
import { Alert } from 'components/shared/Alert';
import { Label } from 'components/shared/Label';
import { FLIGHTS, getAlertWithAttention } from 'constants/texts';
import i18n from 'i18n';
import { formatDate } from 'utils/date';
import { getTranslationDateFormat } from 'utils/i18n/date';
import { isNotDefined } from 'utils/ramda';

import { FlightsContext } from '../../context';
import s from './index.module.css';

export function FlightEmpty() {
  return (
    <div className={cx(itemStyles.blockItem, itemStyles.details)}>
      <strong>Flight departs John F. Kennedy International Airport (JFK)</strong>
      <div className={s.timeText}>{`Departure Time: ${getMinutesAndHoursFormat(new Date(), '4', ':')}`}</div>
      <div>Airline: Delta Airlines</div>
      <div>Flight Number: DL2238</div>
    </div>
  );
}

export function FlightTop(props) {
  const {
    departureFrom,
    arrivalTime,
    departureTime,
    flightNumber,
    bookedBy,
    confirmationNumber,
    duration,
    provider,
    departureAirport,
    fromFsService,
    airline,
    departureTimeEmpty,
    newArrivalTime,
    newDepartureTime,
    flightCancelled,
    isReadonly,
    updateClientSide,
    isPDF,
  } = props;
  const flightDepartsText = fromFsService ? `${departureAirport?.name} ${departureAirport?.fsCode}` : departureFrom;
  const needShowTime = fromFsService ? true : !departureTimeEmpty;
  const isNewArrivalTime = !isNotDefined(newArrivalTime);
  const isNewDepartureTime = !isNotDefined(newDepartureTime);
  const isRescheduled = isNewDepartureTime || isNewArrivalTime;
  const { checkPolicy } = useContext(FlightsContext);
  const isIDL = differenceInCalendarDays(new Date(departureTime), new Date(arrivalTime)) > 0;
  /*
   If it's a PDF or client facing (checkPolicy) -> check updateClientSide
   Otherwise -> check isReadonly (false in Flights tab, true in builder)
   */
  const showLabels = isPDF || checkPolicy ? updateClientSide : isReadonly;
  // Same conditions, but time needs to be displayed in both the flight tab and the builder
  const showChangedTime = isPDF || checkPolicy ? updateClientSide : true;
  // Show only in the flights tab
  const showInFlightsTab = !isReadonly && !checkPolicy;

  return (
    <div className={cx(itemStyles.blockItem, itemStyles.details)}>
      {/* Show these labels on the client-facing / PDF only if updateClientSide is true */}
      {showLabels && (
        <>
          {flightCancelled && (
            <Label
              color="#CE4622"
              text={i18n.t('proposal_content.event_details.flights.tags.cancelled')}
              className={s.label}
            />
          )}
          {/* Show "Flight Change" label only if the flight is not cancelled */}
          {isRescheduled && !flightCancelled && (
            <Label
              color="#1624C2"
              text={i18n.t('proposal_content.event_details.flights.tags.changed')}
              className={s.label}
            />
          )}
        </>
      )}
      {!showInFlightsTab && isIDL && (
        <Label
          color="#65ADFF"
          text={i18n.t('proposal_content.event_details.flights.tags.date_line')}
          className={cx(s.label)}
        />
      )}
      <strong>{`${i18n.t('proposal_content.event_details.flights.flight_departs')} ${flightDepartsText}`}</strong>
      <div className={s.timeText}>
        {i18n.t('proposal_content.event_details.flights.departures.time')}:{' '}
        <FlightTime
          oldTime={new Date(departureTime)}
          newTime={isNewDepartureTime && new Date(newDepartureTime[newDepartureTime.length - 1])}
          utc={departureAirport?.utcOffsetHours}
          config={{ flightCancelled, fromFsService, needShowTime, hideChanges: !showChangedTime }}
        />
      </div>

      {airline && <div>{`${i18n.t('proposal_content.event_details.flights.airline')}: ${airline?.name}`}</div>}
      {provider && <div>{`${i18n.t('proposal_content.event_details.flights.airline')}: ${provider}`}</div>}
      {flightNumber && (
        <div>{`${i18n.t('proposal_content.event_details.flights.number')}: ${
          airline?.fsCode || ''
        }${flightNumber}`}</div>
      )}
      {bookedBy && <div>{`${i18n.t('proposal_content.event_details.booked_by')}: ${bookedBy}`}</div>}
      {confirmationNumber && (
        <div>{`${i18n.t('proposal_content.event_details.confirmation_number')}: ${confirmationNumber}`}</div>
      )}
      {duration && <div>{`${i18n.t('proposal_content.event_details.duration')}: ${duration}`}</div>}
    </div>
  );
}

export function FlightBottom(props) {
  const {
    arrivalAirport,
    arrivalTime,
    departureTime,
    arrivalTo,
    fromFsService,
    arrivalTimeEmpty,
    isReadonly,
    flightCancelled,
    newArrivalTime,
    newDepartureTime,
    updateClientSide,
    isPDF,
    dateFormat,
  } = props;
  const time = new Date(arrivalTime);
  const flightArrivesText = fromFsService ? `${arrivalAirport?.name} ${arrivalAirport?.fsCode}` : arrivalTo;
  const needShowTime = fromFsService ? true : !arrivalTimeEmpty;
  const isNewArrivalTime = !isNotDefined(newArrivalTime);
  const isNewDepartureTime = !isNotDefined(newDepartureTime);
  const isRescheduled = isNewDepartureTime || isNewArrivalTime;
  const { checkPolicy } = useContext(FlightsContext);
  const showChangedTime = isPDF || checkPolicy ? updateClientSide : true;
  const isSameDate =
    isRescheduled && !flightCancelled && showChangedTime
      ? formatDate(new Date(isNewArrivalTime ? newArrivalTime[newArrivalTime.length - 1] : arrivalTime)) ===
        formatDate(new Date(isNewDepartureTime ? newDepartureTime[newDepartureTime.length - 1] : departureTime))
      : formatDate(time) === formatDate(new Date(departureTime));

  return (
    <div className={cx(itemStyles.blockItem, itemStyles.details)}>
      <strong>{`${i18n.t('proposal_content.event_details.flights.flight_arrives')} ${flightArrivesText}`}</strong>
      <div className={s.timeText}>
        {!isSameDate && (
          <>
            {i18n.t('proposal_content.event_details.flights.arrivals.day')}:{' '}
            {i18n.strftime(
              new Date(isNewArrivalTime && showChangedTime ? newArrivalTime[newArrivalTime.length - 1] : arrivalTime),
              getTranslationDateFormat(dateFormat),
            )}
            <br />
          </>
        )}
        {i18n.t('proposal_content.event_details.flights.arrivals.time')}:{' '}
        <FlightTime
          oldTime={time}
          newTime={isNewArrivalTime && new Date(newArrivalTime[newArrivalTime.length - 1])}
          needShowTime={needShowTime}
          utc={arrivalAirport?.utcOffsetHours}
          config={{ fromFsService, flightCancelled, needShowTime, hideChanges: !showChangedTime }}
        />
      </div>
      {/* Display only in the flights tab */}
      {!isReadonly && !checkPolicy && (flightCancelled || isRescheduled) ? (
        <Alert
          className={s.alert}
          variant="danger"
          content={[getAlertWithAttention(FLIGHTS.ALERTS.CHANGE), FLIGHTS.ALERTS.CHECK_INFO]}
        />
      ) : null}
    </div>
  );
}

const FlightTime = ({ oldTime, newTime, utc, config }) => {
  // If the flight has not been rescheduled -> show the old way
  if (!newTime || !config.fromFsService || config.flightCancelled || config.hideChanges) {
    return config.needShowTime ? getMinutesAndHoursFormat(oldTime, utc, ':') : i18n.t('proposal_content.tbd');
  }

  const dayDifference = differenceInCalendarDays(newTime, oldTime);

  // It is necessary to "del" only the time part, UTC part should stay the same
  const [timePart, utcPart] = getMinutesAndHoursFormat(oldTime, utc, ':').split('|');

  return (
    <>
      <strong className={s.flightTime}>
        {getMinutesAndHoursFormat(new Date(newTime), null, ':')}{' '}
        {dayDifference ? (
          <sup>
            {dayDifference > 0 ? '+' : '-'}
            {i18n.t('proposal_content.days', { count: Math.abs(dayDifference) })}
          </sup>
        ) : null}
      </strong>{' '}
      <del>{timePart.trim()}</del>
      {utcPart && ` |${utcPart}`}
    </>
  );
};
